/**
 * @file quotes.api.js
 * @module QuotesAPI
 * @description HTTP API wrapper for quote-related endpoints (create, read, update, cancel, export, etc.).
 * All methods return an Axios Promise created by axiosInstance(token).
 */

import { axiosInstance } from "./axios";

export const QuotesAPI = {
    /**
     * Get a list of quotes.
     * @param {Object} queryParams - Query parameters for filtering/sorting.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise.
     */
    getQuotes: (limit = 10, page = 1, query = "", token = "") => {
        return axiosInstance(token).get(`/quotes?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Create a new quote.
     * @param {Object} quoteData - Data for the new quote.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise.
     */
    createQuote: (quoteData, token = "") => {
        return axiosInstance(token).post("/quotes", quoteData);
    },

    /**
     * Bulk delete quotes by IDs.
     * @param {string[]} quoteIds - Array of quote IDs to delete.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise.
     */
    bulkDeleteQuotes: (payload, token = "") => {
        return axiosInstance(token).delete("/quotes", { data: payload });
    },

    /**
     * Get a specific quote by ID.
     * @param {string} quoteId - ID of the quote to retrieve.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise.
     */
    getQuoteById: (quoteId, token = "") => {
        return axiosInstance(token).get(`/quotes/${quoteId}`);
    },

    /**
     * Update an existing quote.
     * @param {string} quoteId - ID of the quote to update.
     * @param {Object} updateData - Data to update the quote with.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise.
     */
    updateQuoteById: (quoteId, updateData, token = "") => {
        return axiosInstance(token).put(`/quotes/${quoteId}`, updateData);
    },

    /**
     * Delete a quote by ID.
     * @param {string} quoteId - ID of the quote to delete.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise.
     */
    deleteQuoteById: (quoteId, token = "") => {
        return axiosInstance(token).delete(`/quotes/${quoteId}`);
    },

    /**
     * Cancel a quote by ID.
     * @param {string} quoteId - ID of the quote to cancel.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise.
     */
    cancelQuoteById: (quoteId, token = "") => {
        return axiosInstance(token).patch(`/quotes/${quoteId}/cancel`);
    },
    /**
     * Get histories associated with a specific quote ID.
     * @param {string} quoteId - Quote identifier.
     * @param {number} limit - Page size.
     * @param {number} page - Page number.
     * @param {string} [token=""] - Optional authentication token.
    * @returns {Promise} Axios promise resolving to histories list.
  */
    getHistoriesByQuoteId: (quoteId, limit, page, token = "") => {
        return axiosInstance(token).get(`/modules/quotes/${quoteId}/histories?limit=${limit}&page=${page}`)
    },
    /**
     * Update a history entry by id.
     * @param {string} historyId - History entry id.
     * @param {Object} history - Updated history data.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated history.
     */
    updateHistoryById: (historyId, history, token = "") => {
        return axiosInstance(token).put(`/histories/${historyId}`, history)
    },
    /**
     * Download quote PDF by ID.
     * @param {string} quoteId - ID of the quote to download.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise with blob response.
     */
    downloadQuotePDFById: (quoteId, token = "") => {
        return axiosInstance(token).get(`/quotes/${quoteId}/download-pdf`, { responseType: 'blob' });
    },

    /**
     * Create a booking from a quote by ID.
     * @param {string} quoteId - ID of the quote to convert.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise.
     */
    createBookingFromQuoteById: (quoteId, token = "") => {
        return axiosInstance(token).post(`/quotes/${quoteId}/convert`);
    },

    /**
     * Bulk create bookings from quotes.
     * @param {Object} payload - Payload containing quote IDs to convert.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise.
     */
    createBookingsFromQuotesBulk: (payload, token = "") => {
        return axiosInstance(token).post(`/quotes/bookings/bulk`, payload);
    }

    /**
     * Export quotes based on provided payload.
     * @param {Object} payload - Payload containing export parameters.
     * @param {string} token - Optional authentication token.
     * @returns {Promise} - Axios Promise.
     */
    ,
    exportQuotes: (payload = {}, token = "") => {
        return axiosInstance(token).post(`/quotes/exports`, payload);
    }
};
